import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, CardHeader, DatePicker, ErrorState, Input, PageHeader, PageLoader, Select, Switch, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { LineItemsEditor, lineError, newLine, toApiLine, toEditLine, EditLine } from '../../../components/common/LineItemsEditor/LineItemsEditor';
import { CustomerPicker } from '../../../components/common/Pickers/Pickers';
import { FormSection, KeyValues, TableSection, TotalsBox, TwoColumn } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { withHomeState } from '../../../constants/location';
import { masterOptions } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { money } from '../../../utils/currency';
import { toISODate } from '../../../utils/date';
import { calcLine, computeTotals } from '../../../lib/pricing';
import { loadSettings } from '../../settings/services/settingsService';
import { quotationService } from '../services/quotationService';
import { customerService } from '../../customers/services/customerService';
import { templateService } from '../../templates/services/templateService';
import type { ExpandedTemplate } from '../../templates/types/templateTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';
import type { QuoteTemplate } from '../../templates/types/templateTypes';

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toISODate(d);
};
const isoOf = (d) => (d ? toISODate(new Date(d)) : '');

const EMPTY = {
  customerId: null, customerName: '', title: '', serviceType: 'new_wiring', date: toISODate(new Date()), validTill: '',
  siteLine: '', siteCity: '', sitePincode: '', isGst: true, terms: '', notes: '',
};

function TemplatePicker({ onLoad }: { onLoad: (t: ExpandedTemplate) => void }) {
  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    templateService.list({ limit: 100 }).then((r) => setTemplates(r.data.filter((t) => t.isActive !== false))).catch(() => {});
  }, []);
  if (!templates.length) return null;
  return (
    <div className="w-56">
      <Select
        size="sm"
        aria-label="Load a template"
        placeholder={loading ? 'Loading…' : 'Load a template'}
        options={templates.map((t) => ({ value: t._id, label: t.name }))}
        value={null}
        onChange={async (id) => {
          setLoading(true);
          try {
            onLoad(await templateService.expand(id));
          } finally {
            setLoading(false);
          }
        }}
      />
    </div>
  );
}

export default function QuotationForm() {
  const { id = '' } = useParams();
  const editing = !!id;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { form, setForm, set, errors, setErrors } = useForm(EMPTY);
  const [labour, setLabour] = useState<EditLine[]>([]);
  const [materials, setMaterials] = useState<EditLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [validityDays, setValidityDays] = useState(15);
  useMasters();

  const { data: quote, loading, error, reload } = useFetch(
    () => (editing ? quotationService.get(id) : Promise.resolve(null)),
    [id],
  );
  useCrumb(`/quotations/${id}`, quote?.quotationNo);

  // New quotation: company defaults, and the customer when coming from a customer page.
  useEffect(() => {
    if (editing) return;
    loadSettings()
      .then((s) => {
        setValidityDays(s.quotationValidityDays ?? 15);
        setForm((f) => ({ ...f, isGst: !!s.gstin, terms: f.terms || s.quotationTerms || '' }));
      })
      .catch(() => {});
    const customerId = params.get('customerId');
    if (customerId) customerService.get(customerId).then((c) => pickCustomer(customerId, c)).catch(() => {});
    setLabour([newLine({ unit: 'point' })]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  useEffect(() => {
    if (!quote) return;
    setForm({
      customerId: quote.customerId, customerName: quote.customer?.name ?? '', title: quote.title ?? '', serviceType: quote.serviceType ?? 'new_wiring',
      date: isoOf(quote.date), validTill: isoOf(quote.validTill), siteLine: quote.siteAddress?.line ?? '', siteCity: quote.siteAddress?.city ?? '',
      sitePincode: quote.siteAddress?.pincode ?? '', isGst: quote.isGst, terms: quote.terms ?? '', notes: quote.notes ?? '',
    });
    setLabour(quote.labourItems.map((l) => toEditLine(l, 'description')));
    setMaterials(quote.materialItems.map((m) => toEditLine(m, 'name')));
  }, [quote, setForm]);

  function pickCustomer(cid, c) {
    setForm((f) => ({
      ...f,
      customerId: cid,
      customerName: c?.name ?? '',
      // Site defaults to the customer's address; edit it if the work is elsewhere.
      siteLine: f.siteLine || c?.siteAddresses?.[0]?.line || c?.billingAddress?.line || '',
      siteCity: f.siteCity || c?.siteAddresses?.[0]?.city || c?.billingAddress?.city || '',
      sitePincode: f.sitePincode || c?.siteAddresses?.[0]?.pincode || c?.billingAddress?.pincode || '',
    }));
    setErrors((e) => ({ ...e, customerId: undefined }));
  }

  const charged = useMemo(() => [...labour, ...materials.filter((m) => m.supplyBy === 'us')].map((l) => ({ ...l, qty: Number(l.qty) })), [labour, materials]);
  const totals = computeTotals(charged, form.isGst);
  const labourTotal = labour.reduce((a, l) => a + calcLine({ ...l, qty: Number(l.qty) }, form.isGst).amount, 0);
  const materialTotal = totals.subTotal + totals.totalTax - labourTotal;

  const loadTemplate = (t) => {
    const fromT = (arr, key) => arr.map((l) => toEditLine({ ...l, [key]: l[key] ?? l.name ?? l.description }, key));
    setLabour((ls) => [...ls.filter((l) => l.itemId || l.text.trim()), ...fromT(t.labourItems, 'description')]);
    setMaterials((ms) => [...ms.filter((m) => m.itemId || m.text.trim()), ...fromT(t.materialItems, 'name')]);
    if (!form.title) setForm((f) => ({ ...f, title: t.name }));
    toast.success(`Loaded "${t.name}"`);
  };

  const back = () => navigate(editing ? `/quotations/${id}` : '/quotations');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.customerId) errs.customerId = 'Pick the customer';
    const lineMsg = lineError(labour) || lineError(materials);
    if (!labour.length && !materials.length) errs.lines = 'Add at least one labour or material line';
    else if (lineMsg) errs.lines = lineMsg;
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error(errs.lines || 'Please fix the highlighted fields');
      return;
    }
    const body = {
      customerId: form.customerId,
      date: form.date || undefined,
      validTill: form.validTill || undefined,
      title: trimOrUndef(form.title),
      serviceType: form.serviceType,
      isGst: form.isGst,
      siteAddress: withHomeState({ line: form.siteLine.trim(), city: form.siteCity.trim(), pincode: form.sitePincode.trim() }),
      labourItems: labour.map((l) => toApiLine(l, 'description')),
      materialItems: materials.map((m) => toApiLine(m, 'name', { supplyBy: true })),
      terms: trimOrUndef(form.terms),
      notes: trimOrUndef(form.notes),
    };
    setSaving(true);
    try {
      const data = id ? await quotationService.update(id, body) : await quotationService.create(body);
      toast.success(editing ? 'Quotation updated' : `Quotation ${data.quotationNo} saved`);
      navigate(`/quotations/${data._id}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (editing && loading && !quote) return <PageLoader />;
  if (quote && !['draft', 'sent'].includes(quote.status)) {
    return <ErrorState error={{ message: `A ${quote.status} quotation cannot be edited. Duplicate it instead.` }} />;
  }

  const validTill = form.validTill || (form.date ? addDays(form.date, validityDays) : '');
  const actions = (
    <>
      <Button variant="outline" onClick={back} disabled={saving}>Cancel</Button>
      <Button type="submit" form="quote-form" leftIcon="check" loading={saving}>{editing ? 'Save changes' : 'Save as draft'}</Button>
    </>
  );

  return (
    <>
      <PageHeader title={editing ? `Edit ${quote?.quotationNo ?? 'quotation'}` : 'New quotation'} subtitle={editing ? quote?.customer?.name : 'Labour and material list for the customer. Saved as a draft first.'} actions={actions} />
      <form id="quote-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Customer and job">
                <CustomerPicker required value={form.customerId} selectedLabel={form.customerName} onChange={pickCustomer} error={errors.customerId} containerClassName="sm:col-span-2" footer={
                  <Button variant="ghost" size="sm" leftIcon="plus" fullWidth className="justify-start text-brand-600" onClick={() => navigate('/customers/new')}>Add a new customer</Button>
                } />
                <Input label="Job title" value={form.title} onChange={set('title')} placeholder="e.g. 2BHK new wiring" />
                <Select label="Type of work" options={masterOptions('serviceType', { keep: form.serviceType })} value={form.serviceType} onChange={set('serviceType')} searchable={false} />
                <DatePicker label="Quotation date" value={form.date} onChange={set('date')} clearable={false} />
                <DatePicker label="Valid until" value={form.validTill} onChange={set('validTill')} min={form.date || undefined} placeholder={validTill ? `Default: ${validTill.split('-').reverse().join('-')}` : 'Pick a date'} hint={`Leave empty for ${validityDays} days`} />
              </FormSection>

              <FormSection title="Site address" subtitle="Where the work will be done (Maharashtra)">
                <Textarea label="Flat / building / street" rows={2} value={form.siteLine} onChange={set('siteLine')} containerClassName="sm:col-span-2" />
                <Input label="City" value={form.siteCity} onChange={set('siteCity')} />
                <Input label="Pincode" inputMode="numeric" maxLength={6} value={form.sitePincode} onChange={set('sitePincode')} />
              </FormSection>

              <TableSection title="Fitting and labour" subtitle="Work you will do" action={<TemplatePicker onLoad={loadTemplate} />}>
                <LineItemsEditor lines={labour} onChange={setLabour} isGst={form.isGst} itemType="service" textLabel="Work" addLabel="Add work" emptyText="No labour lines. Add work, or load a template." />
              </TableSection>

              <TableSection title="Material list" subtitle="Mark lines the customer will buy themselves; they are listed but not charged">
                <LineItemsEditor lines={materials} onChange={setMaterials} isGst={form.isGst} itemType="material" textLabel="Material" supplyBy addLabel="Add material" emptyText="No materials. Add them if you supply material or want to give a list." />
              </TableSection>

              <FormSection title="Terms and notes">
                <Textarea label="Terms" rows={4} value={form.terms} onChange={set('terms')} maxLength={3000} containerClassName="sm:col-span-2" hint="Printed on the quotation. Default comes from Settings." />
                <Textarea label="Internal notes" rows={2} value={form.notes} onChange={set('notes')} maxLength={2000} containerClassName="sm:col-span-2" hint="Not printed" />
              </FormSection>
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <>
              <Card>
                <CardHeader title="Summary" />
                <div className="space-y-4 p-5 pt-4">
                  <Switch label="GST quotation" description="Adds CGST + SGST on each line" checked={form.isGst} onChange={set('isGst')} />
                  <TotalsBox
                    totals={totals}
                    isGst={form.isGst}
                    lead={[
                      ['Fitting & labour', money(labourTotal)],
                      ['Material by Electroz', money(materialTotal)],
                    ]}
                  />
                </div>
              </Card>
              <Card>
                <CardHeader title="Lines" />
                <div className="p-5 pt-4">
                  <KeyValues
                    rows={[
                      ['Labour lines', labour.length],
                      ['Materials we supply', materials.filter((m) => m.supplyBy === 'us').length],
                      ['Customer supplies', materials.filter((m) => m.supplyBy === 'customer').length],
                    ]}
                  />
                </div>
              </Card>
            </>
          }
        />
      </form>
    </>
  );
}
